import logger from '../utils/logger';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { formatCAD, formatTransactionAmount } from '../utils/currency';
import { 
  exportToJson, importFromJson, removeDuplicates, printContent, 
  createSyncPackage, importSyncPackage, translateText 
} from '../utils/adminUtils';
import { 
  Shield, Users, Wallet, TrendingUp, History, Edit, Loader2, LogOut, DollarSign,
  LayoutDashboard, BookOpen, Plus, Trash2, Save, FolderPlus, RefreshCw, ChevronRight,
  FileText, Settings, Plug, Key, GripVertical, Eye, ArrowUp, ArrowDown, Download, Upload, HelpCircle,
  MessageSquare, Printer, Calendar, Palette, Image, Camera, Bell, Send, CheckCircle, XCircle,
  Globe, Link, Copy, TestTube, Clock, ShoppingCart, Briefcase, MapPin, FileDown, Mail,
  ListTodo, Circle, CheckCircle2, PlayCircle, Menu, X, UserCog, ExternalLink, Heart, Handshake, Lock
} from 'lucide-react';
import { WooCommerceGuide } from '../components/WooCommerceGuide';
import DesignEditor from '../components/admin/DesignEditor';
import MediaManager from '../components/admin/MediaManager';
import PagesManager from '../components/admin/PagesManager';
import ScreenshotsManager from '../components/admin/ScreenshotsManager';
import AdminSidebar from '../components/admin/AdminSidebar';
// Refactored Admin Components
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminUsers from '../components/admin/AdminUsers';
import AdminTransactions from '../components/admin/AdminTransactions';
import AdminWallets from '../components/admin/AdminWallets';
import AdminAgencies from '../components/admin/AdminAgencies';
import AdminTodo from '../components/admin/AdminTodo';
import AdminKnowledge from '../components/admin/AdminKnowledge';
import AdminNotes from '../components/admin/AdminNotes';
import AdminWooCommerce from '../components/admin/AdminWooCommerce';
import AdminTeam from '../components/admin/AdminTeam';
import AdminSettings from '../components/admin/AdminSettings';
import AdminJoinUsEditor from '../components/admin/AdminJoinUsEditor';
import AdminIntegrations from '../components/admin/AdminIntegrations';
import AdminJobPostings from '../components/admin/AdminJobPostings';
import { ExportImportButtons, PrintButton } from '../components/admin/ExportImportButtons';
import { AdminProvider } from '../components/admin/AdminContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_ICONS = {
  FileText: FileText,
  Settings: Settings,
  Plug: Plug,
  Key: Key,
  BookOpen: BookOpen,
  Users: Users,
  Wallet: Wallet,
};

export default function AdminPage() {
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem('payhub_admin_key') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard State
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  
  // User Edit State
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editingUserData, setEditingUserData] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  
  // Knowledge Base State
  const [kbSections, setKbSections] = useState([]);
  const [kbArticles, setKbArticles] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleDialog, setArticleDialog] = useState(false);
  const [sectionDialog, setSectionDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [showWooGuide, setShowWooGuide] = useState(false);

  // Admin Notes State
  const [adminNotes, setAdminNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteDialog, setNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  
  // Transfer to Knowledge Base State
  const [transferToKBDialog, setTransferToKBDialog] = useState(false);
  const [selectedKBSection, setSelectedKBSection] = useState('');
  const [newKBSection, setNewKBSection] = useState('');
  const [transferringNote, setTransferringNote] = useState(false);

  // WooCommerce & IPN State
  const [apiKeys, setApiKeys] = useState([]);
  const [ipnLogs, setIpnLogs] = useState([]);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [ipnConfigDialog, setIpnConfigDialog] = useState(false);
  const [ipnUrl, setIpnUrl] = useState('');
  const [newApiKeyDialog, setNewApiKeyDialog] = useState(false);
  const [newShopName, setNewShopName] = useState('');
  const [newShopUrl, setNewShopUrl] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);

  // Site Contact Email State
  const [siteContactEmail, setSiteContactEmail] = useState('internetpme@gmail.com');
  const [savingContactEmail, setSavingContactEmail] = useState(false);

  // Contribution Config State
  const [contributionConfig, setContributionConfig] = useState({
    enabled: true,
    default_type: 'percent',
    default_percent: 2.0,
    default_amount: 1.0,
    custom_amount_default: false,
    title: { fr: 'Contribution volontaire à PayHub Québec+', en: 'Voluntary contribution to PayHub Quebec+' },
    description: { fr: 'Merci de soutenir votre plateforme', en: 'Thank you for supporting your platform' }
  });
  const [savingContribution, setSavingContribution] = useState(false);

  // Agencies & Invitations State
  const [agencies, setAgencies] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [invitationDialog, setInvitationDialog] = useState(false);
  const [newInvitation, setNewInvitation] = useState({ email: '', agency_name: '', message: '' });
  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);

  // Checklist / To Do State
  const [checklist, setChecklist] = useState([]);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'P2', category: 'feature' });
  const [checklistLoading, setChecklistLoading] = useState(false);

  // Sidebar State
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const api = useMemo(() => axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey },
    withCredentials: true
  }), [adminKey]);

  // Authentication
  const handleLogin = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/stats');
      if (response.data) {
        setIsAuthenticated(true);
        localStorage.setItem('payhub_admin_key', adminKey);
        setStats(response.data);
        fetchAllData();
        toast.success(t('admin.admin_access_granted'));
      }
    } catch (error) {
      toast.error(t('admin.admin_invalid_key'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('payhub_admin_key');
    setIsAuthenticated(false);
    setAdminKey('');
  };

  // Data Fetching
  const fetchAllData = async () => {
    await Promise.all([fetchStats(), fetchUsers(), fetchTransactions(), fetchKB(), fetchNotes(), fetchApiKeys(), fetchSiteContactEmail(), fetchChecklist(), fetchContributionConfig(), fetchAgencies(), fetchInvitations()]);
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      logger.error('Error:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      logger.error('Error:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await api.get('/admin/transactions');
      setTransactions(response.data.transactions || []);
    } catch (error) {
      logger.error('Error:', error);
    }
  };

  const fetchKB = async () => {
    try {
      const [sectionsRes, articlesRes] = await Promise.all([
        api.get('/admin/kb/sections'),
        api.get('/admin/kb/articles')
      ]);
      setKbSections(sectionsRes.data.sections || []);
      setKbArticles(articlesRes.data.articles || []);
    } catch (error) {
      logger.error('Error:', error);
    }
  };

  // WooCommerce & IPN Functions
  const fetchApiKeys = async () => {
    try {
      const response = await api.get('/admin/woocommerce/api-keys');
      setApiKeys(response.data.api_keys || []);
    } catch (error) {
      logger.error('Error fetching API keys:', error);
    }
  };

  const fetchIpnLogs = async (apiKey) => {
    try {
      const response = await axios.get(`${API_URL}/api/woocommerce/ipn/logs?api_key=${apiKey}&limit=20`);
      setIpnLogs(response.data.logs || []);
    } catch (error) {
      logger.error('Error fetching IPN logs:', error);
    }
  };

  const createApiKey = async () => {
    if (!newShopName) {
      toast.error('Le nom de la boutique est requis');
      return;
    }
    setCreatingKey(true);
    try {
      // We need a user token, so we'll use admin endpoint
      const response = await api.post('/admin/woocommerce/create-api-key', {
        shop_name: newShopName,
        shop_url: newShopUrl
      });
      toast.success('Clé API créée! Copiez-la maintenant, elle ne sera plus affichée.');
      setSelectedApiKey(response.data);
      setNewApiKeyDialog(false);
      setNewShopName('');
      setNewShopUrl('');
      fetchApiKeys();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreatingKey(false);
    }
  };

  const configureIpn = async () => {
    if (!selectedApiKey || !ipnUrl) {
      toast.error('URL IPN requise');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/woocommerce/ipn/configure`, {
        api_key: selectedApiKey.api_key,
        ipn_url: ipnUrl
      });
      toast.success('IPN configuré avec succès!');
      setSelectedApiKey({ ...selectedApiKey, ...response.data });
      setIpnConfigDialog(false);
      fetchApiKeys();
    } catch (error) {
      toast.error('Erreur lors de la configuration');
    }
  };

  const testIpn = async (apiKey) => {
    try {
      await axios.post(`${API_URL}/api/woocommerce/ipn/test?api_key=${apiKey}`);
      toast.success('Test IPN envoyé!');
      setTimeout(() => fetchIpnLogs(apiKey), 2000);
    } catch (error) {
      toast.error('Erreur lors du test');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié!');
  };

  // Site Contact Email Functions
  const fetchSiteContactEmail = async () => {
    try {
      const response = await api.get('/admin/site-config');
      if (response.data?.contact_email) {
        setSiteContactEmail(response.data.contact_email);
      }
    } catch (error) {
      logger.error('Error fetching site config:', error);
    }
  };

  const saveSiteContactEmail = async () => {
    setSavingContactEmail(true);
    try {
      await api.put('/admin/site-config', { contact_email: siteContactEmail });
      toast.success(language === 'fr' ? 'Email de contact mis à jour!' : 'Contact email updated!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving');
    } finally {
      setSavingContactEmail(false);
    }
  };

  // ==================== WRAPPER FUNCTIONS FOR UTILITIES ====================
  
  // Wrapper for exportData using imported function
  const exportData = (data, filename, fieldsToExport = null) => {
    exportToJson(data, filename, language, fieldsToExport);
  };

  // Wrapper for importData using imported function
  const importData = async (file, existingData, titleField, apiEndpoint, dataTransform = null) => {
    return importFromJson(file, existingData, titleField, api, apiEndpoint, language, dataTransform);
  };

  // Wrapper for removeDuplicatesUtil
  const removeDuplicatesUtil = async (data, titleField, idField, deleteEndpoint, fetchCallback) => {
    return removeDuplicates(data, titleField, idField, api, deleteEndpoint, language, fetchCallback);
  };

  // Wrapper for createSyncPackage
  const handleCreateSyncPackage = async () => {
    return createSyncPackage(api, language);
  };

  // Wrapper for importSyncPackage
  const handleImportSyncPackage = async (file) => {
    return importSyncPackage(file, api, language, 
      { notes: adminNotes, checklist, jobPostings: [] },
      [fetchNotes, fetchChecklist]
    );
  };

  // Render Export/Import/Dedup buttons component
  const renderExportImportButtons = (data, exportFilename, titleField, idField, apiEndpoint, fetchCallback, dataTransform = null) => (
    <ExportImportButtons
      data={data}
      exportFilename={exportFilename}
      titleField={titleField}
      idField={idField}
      apiEndpoint={apiEndpoint}
      api={api}
      fetchCallback={fetchCallback}
      dataTransform={dataTransform}
    />
  );

  // Render Sync/Print/Translate buttons for sections
  const renderUtilityButtons = (sectionName, getPrintContent) => (
    <div className="flex gap-2 flex-wrap mt-2">
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => {
          const { title, content, info } = getPrintContent();
          printContent(title, content, info);
        }}
      >
        <Printer className="h-4 w-4 mr-2" />
        {language === 'fr' ? 'Imprimer' : 'Print'}
      </Button>
    </div>
  );

  // Translation state and function
  const [translating, setTranslating] = useState(false);
  
  const requestTranslation = async (text, targetLang) => {
    setTranslating(true);
    try {
      const sourceLang = targetLang === 'en' ? 'fr' : 'en';
      const translated = translateText(text, sourceLang, targetLang);
      setTranslating(false);
      return translated;
    } catch (err) {
      setTranslating(false);
      toast.error(language === 'fr' ? 'Erreur de traduction' : 'Translation error');
      return text;
    }
  };

  // Checklist / To Do Functions
  const fetchChecklist = async () => {
    try {
      const response = await api.get('/admin/checklist');
      setChecklist(response.data.items || []);
    } catch (error) {
      logger.error('Error fetching checklist:', error);
    }
  };

  // Contribution Config Functions
  const fetchContributionConfig = async () => {
    try {
      const response = await api.get('/admin/contribution-config');
      setContributionConfig(response.data);
    } catch (error) {
      logger.error('Error fetching contribution config:', error);
    }
  };

  const saveContributionConfig = async () => {
    setSavingContribution(true);
    try {
      await api.put('/admin/contribution-config', contributionConfig);
      toast.success(language === 'fr' ? 'Configuration sauvegardée!' : 'Configuration saved!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving');
    } finally {
      setSavingContribution(false);
    }
  };

  // ================== AGENCIES & INVITATIONS ==================

  const fetchAgencies = async () => {
    try {
      const response = await api.get('/agencies');
      setAgencies(response.data.agencies || []);
    } catch (error) {
      logger.error('Error fetching agencies:', error);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await api.get('/agencies/invitations');
      setInvitations(response.data.invitations || []);
    } catch (error) {
      logger.error('Error fetching invitations:', error);
    }
  };

  const createInvitation = async () => {
    if (!newInvitation.email) {
      toast.error(language === 'fr' ? 'Email requis' : 'Email required');
      return;
    }
    setCreatingInvitation(true);
    try {
      const response = await api.post('/agencies/invitations', {
        ...newInvitation,
        send_email: true,
        language: language
      });
      
      if (response.data.email_sent) {
        toast.success(language === 'fr' ? 'Invitation créée et email envoyé!' : 'Invitation created and email sent!');
      } else {
        toast.success(language === 'fr' ? 'Invitation créée!' : 'Invitation created!');
        // Copy link to clipboard as fallback
        navigator.clipboard.writeText(response.data.invitation_link);
        setCopiedLink(response.data.invitation_link);
        toast.info(language === 'fr' ? 'Lien copié dans le presse-papiers!' : 'Link copied to clipboard!');
      }
      
      setInvitationDialog(false);
      setNewInvitation({ email: '', agency_name: '', message: '' });
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setCreatingInvitation(false);
    }
  };

  const cancelInvitation = async (invitationId) => {
    if (!window.confirm(language === 'fr' ? 'Annuler cette invitation?' : 'Cancel this invitation?')) return;
    try {
      await api.delete(`/agencies/invitations/${invitationId}`);
      toast.success(language === 'fr' ? 'Invitation annulée' : 'Invitation cancelled');
      fetchInvitations();
    } catch (error) {
      toast.error('Error');
    }
  };

  const copyInvitationLink = (code) => {
    const link = `${window.location.origin}/agency/register?code=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(code);
    toast.success(language === 'fr' ? 'Lien copié!' : 'Link copied!');
    setTimeout(() => setCopiedLink(null), 3000);
  };

  const initChecklist = async () => {
    setChecklistLoading(true);
    try {
      await api.post('/admin/checklist/init');
      await fetchChecklist();
      toast.success(language === 'fr' ? 'Checklist initialisée!' : 'Checklist initialized!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setChecklistLoading(false);
    }
  };

  const addChecklistItem = async () => {
    if (!newTask.title.trim()) {
      toast.error(language === 'fr' ? 'Le titre est requis' : 'Title is required');
      return;
    }
    setChecklistLoading(true);
    try {
      await api.post('/admin/checklist', newTask);
      setNewTask({ title: '', description: '', priority: 'P2', category: 'feature' });
      await fetchChecklist();
      toast.success(language === 'fr' ? 'Tâche ajoutée!' : 'Task added!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setChecklistLoading(false);
    }
  };

  const updateChecklistStatus = async (itemId, newStatus) => {
    try {
      await api.put(`/admin/checklist/${itemId}`, { status: newStatus });
      await fetchChecklist();
      if (newStatus === 'completed') {
        toast.success(language === 'fr' ? 'Tâche complétée!' : 'Task completed!');
      }
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const seedKB = async () => {
    setLoading(true);
    try {
      await api.post('/admin/kb/seed');
      await fetchKB();
      toast.success('Knowledge Base initialisée!');
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    } finally {
      setLoading(false);
    }
  };

  const resetKB = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser la Knowledge Base? Toutes les données seront perdues.')) return;
    setLoading(true);
    try {
      await api.post('/admin/kb/reset');
      await fetchKB();
      setSelectedSection(null);
      setSelectedArticle(null);
      toast.success('Knowledge Base réinitialisée!');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setSelectedUser(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSetBalance = async () => {
    if (!selectedUser || !newBalance) return;
    setLoading(true);
    try {
      await api.put(`/admin/users/${selectedUser.user.user_id}/balance?amount=${parseFloat(newBalance)}`);
      toast.success('Solde mis à jour');
      setBalanceDialog(false);
      setNewBalance('');
      fetchUserDetails(selectedUser.user.user_id);
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  // Open Edit User Dialog
  const openEditUserDialog = (user) => {
    setEditingUserData({
      name: user.name || '',
      email: user.email || '',
      language: user.language || 'fr',
      account_type: user.account_type || 'personal',
      is_active: user.is_active !== false,
      notes: user.admin_notes || ''
    });
    setEditUserDialog(true);
  };

  // Save User Profile
  const handleSaveUserProfile = async () => {
    if (!selectedUser || !editingUserData) return;
    setSavingUser(true);
    try {
      const response = await api.put(`/admin/users/${selectedUser.user.user_id}`, editingUserData);
      toast.success(language === 'fr' ? 'Profil mis à jour!' : 'Profile updated!');
      setEditUserDialog(false);
      // Refresh user data
      setSelectedUser(prev => ({ ...prev, user: response.data.user }));
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update error');
      toast.error(msg);
    } finally {
      setSavingUser(false);
    }
  };

  // Switch User View (Impersonate)
  const handleSwitchUserView = async () => {
    if (!selectedUser) return;
    
    if (!window.confirm(
      language === 'fr' 
        ? `Voulez-vous être connecté en tant que "${selectedUser.user.name}" (${selectedUser.user.email})? Vous serez redirigé vers le dashboard de cet utilisateur.`
        : `Do you want to switch to "${selectedUser.user.name}" (${selectedUser.user.email})? You will be redirected to this user's dashboard.`
    )) return;

    setImpersonating(true);
    try {
      const response = await api.post(`/admin/users/${selectedUser.user.user_id}/impersonate`);
      
      // Impersonation session is set as an httpOnly cookie by the backend.
      // Only store non-sensitive flags for the impersonation banner.
      localStorage.setItem('payhub_impersonating', 'true');
      localStorage.setItem('payhub_impersonated_user', JSON.stringify(response.data.user));
      
      toast.success(
        language === 'fr' 
          ? `Connecté en tant que ${response.data.user.name}` 
          : `Logged in as ${response.data.user.name}`
      );
      
      // Use window.location.href to force full page reload
      // This ensures AuthContext reinitializes with the new token
      window.location.href = '/dashboard';
      
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur d\'impersonation' : 'Impersonation error');
      setImpersonating(false);
    }
  };

  // Section Management
  const handleSaveSection = async () => {
    if (!editingSection?.name) {
      toast.error('Le nom de la section est requis');
      return;
    }
    setLoading(true);
    try {
      if (editingSection.section_id) {
        await api.put(`/admin/kb/sections/${editingSection.section_id}`, editingSection);
        toast.success('Section mise à jour');
      } else {
        await api.post('/admin/kb/sections', editingSection);
        toast.success('Section créée');
      }
      setSectionDialog(false);
      setEditingSection(null);
      fetchKB();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    const articlesInSection = kbArticles.filter(a => a.section_id === sectionId);
    if (!window.confirm(`Supprimer cette section et ses ${articlesInSection.length} articles?`)) return;
    
    try {
      await api.delete(`/admin/kb/sections/${sectionId}`);
      toast.success('Section supprimée');
      if (selectedSection?.section_id === sectionId) {
        setSelectedSection(null);
        setSelectedArticle(null);
      }
      fetchKB();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Article Management
  const handleSaveArticle = async () => {
    if (!editingArticle?.title || !editingArticle?.content || !editingArticle?.section_id) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }
    setLoading(true);
    try {
      if (editingArticle.article_id) {
        await api.put(`/admin/kb/articles/${editingArticle.article_id}`, editingArticle);
        toast.success('Article mis à jour');
      } else {
        await api.post('/admin/kb/articles', editingArticle);
        toast.success('Article créé');
      }
      setArticleDialog(false);
      setEditingArticle(null);
      fetchKB();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Supprimer cet article?')) return;
    try {
      await api.delete(`/admin/kb/articles/${articleId}`);
      toast.success('Article supprimé');
      if (selectedArticle?.article_id === articleId) setSelectedArticle(null);
      fetchKB();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Download WooCommerce Plugin
  const handleDownloadPlugin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/woocommerce-plugin/download`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'payhub-gateway.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Plugin téléchargé!');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Notes Management
  const fetchNotes = async () => {
    try {
      const response = await api.get('/admin/notes');
      setAdminNotes(response.data.notes || []);
    } catch (error) {
      logger.error('Error fetching notes:', error);
    }
  };

  const handleSaveNote = async () => {
    if (!editingNote?.title || !editingNote?.content) {
      toast.error('Le titre et le contenu sont requis');
      return;
    }
    setLoading(true);
    try {
      if (editingNote.note_id) {
        await api.put(`/admin/notes/${editingNote.note_id}`, editingNote);
        toast.success('Note mise à jour');
      } else {
        await api.post('/admin/notes', editingNote);
        toast.success('Note créée');
      }
      setNoteDialog(false);
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note?')) return;
    try {
      await api.delete(`/admin/notes/${noteId}`);
      toast.success('Note supprimée');
      if (selectedNote?.note_id === noteId) setSelectedNote(null);
      fetchNotes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Transfer Note to Knowledge Base
  const openTransferToKBDialog = () => {
    if (!selectedNote) return;
    setSelectedKBSection('');
    setNewKBSection('');
    setTransferToKBDialog(true);
  };

  const handleTransferToKB = async () => {
    if (!selectedNote) return;
    
    const sectionToUse = newKBSection.trim() || selectedKBSection;
    if (!sectionToUse) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner ou créer une section' : 'Please select or create a section');
      return;
    }

    setTransferringNote(true);
    try {
      // If creating new section, create it first
      let sectionId = selectedKBSection;
      if (newKBSection.trim()) {
        const sectionRes = await api.post('/admin/kb/sections', {
          name: newKBSection.trim(),
          description: '',
          order: kbSections.length
        });
        sectionId = sectionRes.data.section_id;
        toast.success(language === 'fr' ? `Section "${newKBSection}" créée` : `Section "${newKBSection}" created`);
      }

      // Create KB article from note
      await api.post('/admin/kb/articles', {
        section_id: sectionId,
        title: selectedNote.title,
        content: selectedNote.content,
        language: selectedNote.language || 'fr',
        is_published: true,
        metadata: {
          source: 'admin_note',
          original_note_id: selectedNote.note_id,
          transferred_at: new Date().toISOString()
        }
      });

      toast.success(language === 'fr' ? 'Note transférée vers la Base de connaissances!' : 'Note transferred to Knowledge Base!');
      setTransferToKBDialog(false);
      fetchKB(); // Refresh KB
    } catch (error) {
      logger.error('Transfer error:', error);
      toast.error(language === 'fr' ? 'Erreur lors du transfert' : 'Transfer error');
    } finally {
      setTransferringNote(false);
    }
  };

  const handlePrintNote = (note) => {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${note.title} - PayHub</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.8;
            color: #1a1a1a;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .header { 
            border-bottom: 3px solid #166534; 
            padding-bottom: 20px; 
            margin-bottom: 30px;
          }
          .logo { 
            display: flex; 
            align-items: center; 
            gap: 12px; 
            margin-bottom: 20px;
          }
          .logo-icon { 
            width: 40px; 
            height: 40px; 
            background: #166534; 
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
          }
          .logo-text { font-size: 24px; font-weight: bold; color: #166534; }
          h1 { font-size: 28px; color: #166534; margin-bottom: 10px; }
          .meta { color: #666; font-size: 14px; margin-bottom: 20px; }
          .meta span { margin-right: 20px; }
          .content { 
            white-space: pre-wrap; 
            font-size: 15px; 
            line-height: 1.8;
            background: #f8fafc;
            padding: 24px;
            border-radius: 8px;
            border-left: 4px solid #166534;
          }
          .footer { 
            margin-top: 40px; 
            padding-top: 20px; 
            border-top: 1px solid #e5e5e5; 
            color: #666; 
            font-size: 12px; 
            text-align: center;
          }
          @media print {
            body { padding: 20px; }
            .content { background: #f8fafc; -webkit-print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">
            <div class="logo-icon">PH</div>
            <div class="logo-text">PayHub Admin</div>
          </div>
          <h1>${note.title}</h1>
          <div class="meta">
            <span>📁 ${note.category || 'Général'}</span>
            <span>🌐 ${note.language === 'fr' ? 'Français' : 'English'}</span>
            <span>📅 ${new Date(note.created_at).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
        <div class="content">${note.content}</div>
        <div class="footer">
          <p>Document généré le ${new Date().toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          <p>PayHub © ${new Date().getFullYear()} - Tous droits réservés</p>
        </div>
      </body>
      </html>
    `;
    // Render via Blob URL (avoids document.write / XSS with user content)
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };
    }
  };

  // Auto-login effect - runs once on mount only (adminKey is restored from localStorage)
  useEffect(() => {
    if (adminKey && localStorage.getItem('payhub_admin_key')) {
      handleLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateStr) => new Date(dateStr).toLocaleString('fr-CA');

  const getSectionArticles = (sectionId) => kbArticles.filter(a => a.section_id === sectionId);

  const getSectionIcon = (iconName) => {
    const Icon = SECTION_ICONS[iconName] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
              <Shield className="h-7 w-7 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl">{t('admin.admin_login_title')}</CardTitle>
            <CardDescription>{t('admin.admin_login_desc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="adminKey">{t('admin.admin_key_label')}</Label>
              <Input
                id="adminKey"
                type="password"
                placeholder={t('admin.admin_key_placeholder')}
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                className="h-12 rounded-xl"
                data-testid="admin-key-input"
              />
            </div>
            <Button onClick={handleLogin} className="w-full h-12 rounded-xl" disabled={loading || !adminKey} data-testid="admin-login-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
              {t('admin.admin_login_btn')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="w-full rounded-xl">
              {t('common.back_to_home')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdminProvider adminKey={adminKey}>
    <div className="min-h-screen bg-background flex">
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <div className={`hidden lg:block fixed left-0 top-0 h-screen z-30 transition-all duration-300 ${sidebarOpen ? 'w-72' : 'w-0 overflow-hidden'}`}>
        <AdminSidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          language={language}
        />
      </div>

      {/* Sidebar - Mobile */}
      <div className={`fixed left-0 top-0 h-screen z-50 lg:hidden transition-transform duration-300 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="relative">
          <AdminSidebar 
            activeTab={activeTab} 
            setActiveTab={(tab) => {
              setActiveTab(tab);
              setMobileSidebarOpen(false);
            }} 
            language={language}
          />
          <button 
            onClick={() => setMobileSidebarOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-lg bg-gray-100 dark:bg-gray-800 lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'lg:ml-72' : 'lg:ml-0'}`}>
        {/* Header */}
        <header className="sticky top-0 z-20 w-full border-b border-border/40 bg-background/95 backdrop-blur">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Mobile menu button */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="lg:hidden"
                  onClick={() => setMobileSidebarOpen(true)}
                >
                  <Menu className="h-5 w-5" />
                </Button>
                {/* Desktop sidebar toggle */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="hidden lg:flex"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <Menu className="h-5 w-5" />
                </Button>
                <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
                  <Shield className="h-5 w-5 text-primary-foreground" />
                </div>
                <span className="font-bold text-xl">PayHub Admin</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Language Toggle */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={toggleLanguage}
                  className="gap-1 font-medium"
                >
                  <Globe className="h-4 w-4" />
                  {language === 'fr' ? 'EN' : 'FR'}
                </Button>
                <Button variant="ghost" size="sm" onClick={fetchAllData}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('admin.logout')}
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
          {/* Dashboard - Using modular component */}
          {activeTab === 'dashboard' && (
            <AdminDashboard 
              setActiveTab={setActiveTab} 
              onDownloadPlugin={handleDownloadPlugin}
              onShowGuide={() => setShowWooGuide(true)}
              stats={stats}
            />
          )}

          {/* To Do / Checklist */}
          {/* Todo - Using modular component */}
          {activeTab === 'todo' && (
            <AdminTodo checklist={checklist} adminKey={adminKey} onRefresh={fetchChecklist} />
          )}

          {/* Team / Job Postings - Using modular component */}
          {(activeTab === 'team' || activeTab === 'positions') && (
            <AdminJobPostings adminKey={adminKey} />
          )}

          {/* WooCommerce & IPN - woo-apikeys, woo-ipn, woo-logs, woo-plugin, woocommerce */}
          {/* WooCommerce & IPN - woo-apikeys, woo-ipn, woo-logs, woo-plugin, woocommerce */}
          {(activeTab === 'woocommerce' || activeTab === 'woo-apikeys' || activeTab === 'woo-ipn' || activeTab === 'woo-logs' || activeTab === 'woo-plugin') && (
          <div className="space-y-6">
            {/* Header with colored gradient */}
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                    <ShoppingCart className="h-5 w-5 text-white" />
                  </div>
                  WooCommerce & IPN
                </h2>
                <p className="text-muted-foreground mt-1">Gérez vos clés API et notifications instantanées</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={fetchApiKeys}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Actualiser
                </Button>
                <Button onClick={() => setNewApiKeyDialog(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle Clé API
                </Button>
              </div>
            </div>

            {/* Plugin Download Card */}
            <Card className="border-2 border-dashed border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                      <Download className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Plugin WooCommerce avec IPN</p>
                      <p className="text-sm text-muted-foreground">Téléchargez la dernière version du plugin (inclut le support IPN)</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowWooGuide(true)} className="gap-2">
                      <HelpCircle className="h-4 w-4" />
                      Guide d'installation
                    </Button>
                    <Button onClick={handleDownloadPlugin} className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
                      <Download className="h-4 w-4" />
                      Télécharger Plugin
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* API Keys Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Key className="h-4 w-4 text-white" />
                  </div>
                  Clés API WooCommerce
                </CardTitle>
                <CardDescription>Gérez les clés API pour vos boutiques WooCommerce</CardDescription>
              </CardHeader>
              <CardContent>
                {apiKeys.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Key className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground mb-4">Aucune clé API créée</p>
                    <Button onClick={() => setNewApiKeyDialog(true)} variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Créer une clé API
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {apiKeys.map((key) => (
                      <div key={key.api_key_id} className={`p-4 rounded-xl border-2 ${key.is_active ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10' : 'border-red-200 bg-red-50/50 dark:bg-red-900/10'}`}>
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${key.is_active ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gradient-to-br from-red-500 to-rose-600'}`}>
                              <ShoppingCart className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold flex items-center gap-2">
                                {key.shop_name}
                                {key.is_active ? (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">Actif</span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">Révoqué</span>
                                )}
                              </p>
                              <p className="text-sm text-muted-foreground font-mono">{key.api_key_masked || key.api_key_id}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* IPN Status */}
                            {key.ipn_url ? (
                              <div className="flex items-center gap-1 text-emerald-600">
                                <CheckCircle className="h-4 w-4" />
                                <span className="text-xs">IPN Configuré</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-amber-600">
                                <XCircle className="h-4 w-4" />
                                <span className="text-xs">IPN Non configuré</span>
                              </div>
                            )}
                            <Button variant="outline" size="sm" onClick={() => { setSelectedApiKey(key); setIpnUrl(key.ipn_url || ''); setIpnConfigDialog(true); }}>
                              <Bell className="h-4 w-4 mr-1" />
                              IPN
                            </Button>
                            {key.ipn_url && (
                              <Button variant="outline" size="sm" onClick={() => testIpn(key.api_key)}>
                                <TestTube className="h-4 w-4 mr-1" />
                                Test
                              </Button>
                            )}
                          </div>
                        </div>
                        {key.ipn_url && (
                          <div className="mt-3 pt-3 border-t border-border/50">
                            <p className="text-xs text-muted-foreground flex items-center gap-2">
                              <Link className="h-3 w-3" />
                              IPN URL: <code className="bg-muted px-2 py-0.5 rounded">{key.ipn_url}</code>
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* IPN Logs Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <History className="h-4 w-4 text-white" />
                  </div>
                  Historique IPN
                </CardTitle>
                <CardDescription>Dernières notifications envoyées aux boutiques</CardDescription>
              </CardHeader>
              <CardContent>
                {ipnLogs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun log IPN disponible</p>
                    <p className="text-sm">Sélectionnez une clé API et configurez l'IPN pour voir les logs</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {ipnLogs.map((log, idx) => (
                      <div key={log.ipn_id || idx} className={`p-3 rounded-lg border ${log.success ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {log.success ? (
                              <CheckCircle className="h-5 w-5 text-emerald-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600" />
                            )}
                            <span className="font-medium">{log.payload?.status || 'unknown'}</span>
                            <span className="text-xs text-muted-foreground">Order #{log.payload?.order_id}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString('fr-CA')}</span>
                        </div>
                        {!log.success && log.response && (
                          <p className="text-xs text-red-600 mt-1 truncate">{log.response}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          )}

          {/* Design / Appearance - design, theme, logo, appearance */}
          {(activeTab === 'design' || activeTab === 'theme' || activeTab === 'logo' || activeTab === 'appearance') && (
            <DesignEditor adminKey={adminKey} />
          )}

          {/* Pages */}
          {activeTab === 'pages' && (
            <PagesManager adminKey={adminKey} />
          )}

          {/* Join Us Page Editor - Using modular component */}
          {activeTab === 'join-us-editor' && (
            <AdminJoinUsEditor adminKey={adminKey} />
          )}

          {/* Medias */}
          {activeTab === 'medias' && (
            <MediaManager adminKey={adminKey} />
          )}

          {/* Screenshots */}
          {activeTab === 'screenshots' && (
            <ScreenshotsManager adminKey={adminKey} />
          )}

          {/* Users - Using modular component */}
          {(activeTab === 'users' || activeTab === 'users-list') && (
            <AdminUsers users={users} adminKey={adminKey} onRefresh={fetchUsers} />
          )}

          {/* Transactions - Using modular component */}
          {(activeTab === 'transactions' || activeTab === 'finances') && (
            <AdminTransactions transactions={transactions} />
          )}

          {/* Wallets - Using modular component */}
          {activeTab === 'wallets' && (
            <AdminWallets users={users} onUserSelect={fetchUserDetails} />
          )}

          {/* ================== AGENCIES SECTION - Using modular component ================== */}
          
          {/* Agencies List & Invitations */}
          {(activeTab === 'agencies' || activeTab === 'agencies-list' || activeTab === 'agencies-invitations') && (
            <AdminAgencies 
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              agencies={agencies}
              invitations={invitations}
              adminKey={adminKey}
              onRefreshAgencies={fetchAgencies}
              onRefreshInvitations={fetchInvitations}
              onSelectAgency={setSelectedAgency}
            />
          )}
          {/* WooCommerce Detailed Views - Using modular component */}
          {(activeTab === 'woo-apikeys' || activeTab === 'woo-ipn' || activeTab === 'woo-logs') && (
            <AdminWooCommerce 
              activeTab={activeTab}
              apiKeys={apiKeys}
              ipnLogs={ipnLogs}
              adminKey={adminKey}
              onRefreshApiKeys={fetchApiKeys}
              onRefreshIpnLogs={fetchIpnLogs}
              onDownloadPlugin={handleDownloadPlugin}
              onShowGuide={() => setShowWooGuide(true)}
            />
          )}

          {/* Settings - Using modular component */}
          {(activeTab === 'config' || activeTab === 'settings') && (
            <AdminSettings 
              contributionConfig={contributionConfig}
              adminKey={adminKey}
              onSaveContributionConfig={saveContributionConfig}
              onCreateSyncPackage={createSyncPackage}
              onImportSyncPackage={importSyncPackage}
            />
          )}

          {/* Settings - Integrations - Using modular component */}
          {activeTab === 'integrations' && (
            <AdminIntegrations />
          )}

          {/* Knowledge Base */}
          {/* Knowledge Base - Using modular component */}
          {activeTab === 'knowledge' && (
            <AdminKnowledge 
              kbSections={kbSections} 
              kbArticles={kbArticles}
              adminKey={adminKey} 
              onRefreshSections={fetchKB}
              onRefreshArticles={fetchKB}
            />
          )}

          {/* Notes */}
          {/* Notes - Using modular component */}
          {activeTab === 'notes' && (
            <AdminNotes 
              notes={adminNotes}
              kbSections={kbSections}
              adminKey={adminKey} 
              onRefresh={fetchNotes}
            />
          )}

        </main>
      </div>

      {/* User Details Dialog - Enhanced */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                {selectedUser?.user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <span>{language === 'fr' ? 'Profil Utilisateur' : 'User Profile'}</span>
                {selectedUser?.user?.is_active === false && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded bg-red-100 text-red-700">
                    {language === 'fr' ? 'Inactif' : 'Inactive'}
                  </span>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg border">
                <Button 
                  onClick={() => openEditUserDialog(selectedUser.user)}
                  className="gap-2"
                  data-testid="edit-user-profile-btn"
                >
                  <UserCog className="h-4 w-4" />
                  {language === 'fr' ? 'Éditer Profil' : 'Edit Profile'}
                </Button>
                <Button 
                  variant="secondary"
                  onClick={handleSwitchUserView}
                  disabled={impersonating}
                  className="gap-2"
                  data-testid="switch-user-view-btn"
                >
                  {impersonating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                  {language === 'fr' ? 'Switch User View' : 'Switch User View'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => { setNewBalance(selectedUser.wallet?.balance?.toString() || '0'); setBalanceDialog(true); }}
                  className="gap-2"
                >
                  <DollarSign className="h-4 w-4" />
                  {language === 'fr' ? 'Modifier Solde' : 'Edit Balance'}
                </Button>
              </div>

              {/* User Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-background">
                  <CardContent className="pt-4 space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                        {language === 'fr' ? 'Nom complet' : 'Full Name'}
                      </Label>
                      <p className="font-semibold text-lg">{selectedUser.user.name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">Email</Label>
                      <p className="font-medium">{selectedUser.user.email}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                        {language === 'fr' ? 'Langue' : 'Language'}
                      </Label>
                      <p className="font-medium">{selectedUser.user.language === 'fr' ? '🇫🇷 Français' : '🇬🇧 English'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                        {language === 'fr' ? 'Type de compte' : 'Account Type'}
                      </Label>
                      <p className="font-medium capitalize">{selectedUser.user.account_type || 'Personal'}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-background">
                  <CardContent className="pt-4 space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">User ID</Label>
                      <p className="font-mono text-sm bg-muted px-2 py-1 rounded">{selectedUser.user.user_id}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                        {language === 'fr' ? 'Fournisseur Auth' : 'Auth Provider'}
                      </Label>
                      <p className="font-medium capitalize">{selectedUser.user.auth_provider || 'Email'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                        {language === 'fr' ? 'Créé le' : 'Created'}
                      </Label>
                      <p className="font-medium">{selectedUser.user.created_at ? formatDate(selectedUser.user.created_at) : 'N/A'}</p>
                    </div>
                    {selectedUser.user.admin_notes && (
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                          {language === 'fr' ? 'Notes Admin' : 'Admin Notes'}
                        </Label>
                        <p className="text-sm text-muted-foreground bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded border-l-2 border-yellow-400">
                          {selectedUser.user.admin_notes}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Wallet Info */}
              {selectedUser.wallet && (
                <Card className="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-emerald-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs text-emerald-600 uppercase tracking-wide">
                          {language === 'fr' ? 'Solde du Wallet' : 'Wallet Balance'}
                        </Label>
                        <p className="text-4xl font-bold text-emerald-700">{formatCAD(selectedUser.wallet.balance)}</p>
                        <p className="text-sm text-muted-foreground">Wallet ID: {selectedUser.wallet.wallet_id}</p>
                      </div>
                      <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Wallet className="h-8 w-8 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Transactions */}
              {selectedUser.transactions && selectedUser.transactions.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <History className="h-4 w-4" />
                      {language === 'fr' ? 'Transactions Récentes' : 'Recent Transactions'}
                      <span className="text-xs text-muted-foreground">({selectedUser.transactions.length})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedUser.transactions.slice(0, 10).map((tx, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded border bg-background text-sm">
                          <div className="flex items-center gap-2">
                            <div className={`h-2 w-2 rounded-full ${tx.amount > 0 ? 'bg-green-500' : 'bg-red-500'}`} />
                            <span className="capitalize">{tx.type?.replace(/_/g, ' ')}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={tx.amount > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                              {tx.amount > 0 ? '+' : ''}{formatCAD(tx.amount)}
                            </span>
                            <span className="text-xs text-muted-foreground">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Profile Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              {language === 'fr' ? 'Éditer Profil Utilisateur' : 'Edit User Profile'}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.user?.email}
            </DialogDescription>
          </DialogHeader>
          {editingUserData && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom' : 'Name'} *</Label>
                <Input 
                  value={editingUserData.name} 
                  onChange={(e) => setEditingUserData({...editingUserData, name: e.target.value})}
                  placeholder="John Doe"
                  data-testid="edit-user-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={editingUserData.email} 
                  onChange={(e) => setEditingUserData({...editingUserData, email: e.target.value})}
                  placeholder="user@example.com"
                  data-testid="edit-user-email-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Langue' : 'Language'}</Label>
                  <Select 
                    value={editingUserData.language} 
                    onValueChange={(v) => setEditingUserData({...editingUserData, language: v})}
                  >
                    <SelectTrigger data-testid="edit-user-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">🇫🇷 Français</SelectItem>
                      <SelectItem value="en">🇬🇧 English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Type de compte' : 'Account Type'}</Label>
                  <Select 
                    value={editingUserData.account_type} 
                    onValueChange={(v) => setEditingUserData({...editingUserData, account_type: v})}
                  >
                    <SelectTrigger data-testid="edit-user-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal">{language === 'fr' ? 'Personnel' : 'Personal'}</SelectItem>
                      <SelectItem value="professional">{language === 'fr' ? 'Professionnel' : 'Professional'}</SelectItem>
                      <SelectItem value="merchant">{language === 'fr' ? 'Marchand' : 'Merchant'}</SelectItem>
                      <SelectItem value="restaurant">{language === 'fr' ? 'Resto-Café' : 'Restaurant'}</SelectItem>
                      <SelectItem value="agency">{language === 'fr' ? 'Agence Locale' : 'Local Agency'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                <input 
                  type="checkbox" 
                  id="user-active"
                  checked={editingUserData.is_active}
                  onChange={(e) => setEditingUserData({...editingUserData, is_active: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="user-active" className="cursor-pointer">
                  {language === 'fr' ? 'Compte actif' : 'Account active'}
                </Label>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Notes Admin (interne)' : 'Admin Notes (internal)'}</Label>
                <Textarea 
                  value={editingUserData.notes} 
                  onChange={(e) => setEditingUserData({...editingUserData, notes: e.target.value})}
                  placeholder={language === 'fr' ? 'Notes visibles uniquement par les admins...' : 'Notes visible only to admins...'}
                  rows={3}
                  data-testid="edit-user-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSaveUserProfile} 
              disabled={savingUser || !editingUserData?.name || !editingUserData?.email}
              data-testid="save-user-profile-btn"
            >
              {savingUser && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Save className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Sauvegarder' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Balance Dialog */}
      <Dialog open={balanceDialog} onOpenChange={setBalanceDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{language === 'fr' ? 'Modifier le Solde' : 'Edit Balance'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Nouveau Solde (CAD)' : 'New Balance (CAD)'}</Label>
              <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input type="number" step="0.01" value={newBalance} onChange={(e) => setNewBalance(e.target.value)} className="pl-10 h-12 rounded-xl" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBalanceDialog(false)}>{language === 'fr' ? 'Annuler' : 'Cancel'}</Button><Button onClick={handleSetBalance} disabled={loading}>{loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{language === 'fr' ? 'Mettre à jour' : 'Update'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Section Dialog */}
      <Dialog open={sectionDialog} onOpenChange={setSectionDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingSection?.section_id ? 'Modifier Section' : 'Nouvelle Section'}</DialogTitle></DialogHeader>
          {editingSection && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>Nom *</Label><Input value={editingSection.name} onChange={(e) => setEditingSection({...editingSection, name: e.target.value})} placeholder="Nom de la section" /></div>
              <div className="space-y-2"><Label>Description</Label><Input value={editingSection.description} onChange={(e) => setEditingSection({...editingSection, description: e.target.value})} placeholder="Description" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icône</Label>
                  <Select value={editingSection.icon} onValueChange={(v) => setEditingSection({...editingSection, icon: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FileText">FileText</SelectItem>
                      <SelectItem value="Settings">Settings</SelectItem>
                      <SelectItem value="Plug">Plug</SelectItem>
                      <SelectItem value="Key">Key</SelectItem>
                      <SelectItem value="BookOpen">BookOpen</SelectItem>
                      <SelectItem value="Users">Users</SelectItem>
                      <SelectItem value="Wallet">Wallet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Ordre</Label><Input type="number" value={editingSection.order} onChange={(e) => setEditingSection({...editingSection, order: parseInt(e.target.value) || 0})} /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSectionDialog(false)}>Annuler</Button><Button onClick={handleSaveSection} disabled={loading}>{loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}<Save className="h-4 w-4 mr-2" />Sauvegarder</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog open={articleDialog} onOpenChange={setArticleDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingArticle?.article_id ? 'Modifier Article' : 'Nouvel Article'}</DialogTitle></DialogHeader>
          {editingArticle && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Titre *</Label><Input value={editingArticle.title} onChange={(e) => setEditingArticle({...editingArticle, title: e.target.value})} placeholder="Titre de l'article" /></div>
                <div className="space-y-2">
                  <Label>Section *</Label>
                  <Select value={editingArticle.section_id} onValueChange={(v) => setEditingArticle({...editingArticle, section_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner une section" /></SelectTrigger>
                    <SelectContent>
                      {kbSections.map(s => (<SelectItem key={s.section_id} value={s.section_id}>{s.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Langue</Label>
                  <Select value={editingArticle.language} onValueChange={(v) => setEditingArticle({...editingArticle, language: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="fr">Français</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Tags (séparés par virgule)</Label><Input value={editingArticle.tags?.join(', ') || ''} onChange={(e) => setEditingArticle({...editingArticle, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} placeholder="tag1, tag2" /></div>
              </div>
              <div className="space-y-2"><Label>Contenu * (Markdown supporté)</Label><Textarea value={editingArticle.content} onChange={(e) => setEditingArticle({...editingArticle, content: e.target.value})} placeholder="Contenu de l'article..." className="min-h-[350px] font-mono text-sm" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setArticleDialog(false)}>Annuler</Button><Button onClick={handleSaveArticle} disabled={loading}>{loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}<Save className="h-4 w-4 mr-2" />Sauvegarder</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNote?.note_id ? (language === 'fr' ? 'Modifier Note' : 'Edit Note') : (language === 'fr' ? 'Nouvelle Note' : 'New Note')}</DialogTitle>
          </DialogHeader>
          {editingNote && (
            <div className="space-y-4">
              {/* Title - Full width */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre' : 'Title'} *</Label>
                <Input 
                  value={editingNote.title} 
                  onChange={(e) => setEditingNote({...editingNote, title: e.target.value})} 
                  placeholder={language === 'fr' ? 'Titre complet de la note' : 'Full note title'}
                  className="text-base font-medium"
                  data-testid="note-title-input"
                />
                <p className="text-xs text-muted-foreground">
                  {editingNote.title?.length || 0} {language === 'fr' ? 'caractères' : 'characters'}
                </p>
              </div>
              
              {/* Category & Language - Side by side */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Catégorie' : 'Category'}</Label>
                  <Select value={editingNote.category} onValueChange={(v) => setEditingNote({...editingNote, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Technical">🔧 Technical</SelectItem>
                      <SelectItem value="conversation">💬 Conversation</SelectItem>
                      <SelectItem value="architecture">🏗️ Architecture</SelectItem>
                      <SelectItem value="development">🔧 {language === 'fr' ? 'Développement' : 'Development'}</SelectItem>
                      <SelectItem value="bug">🐛 Bug Report</SelectItem>
                      <SelectItem value="feature">✨ Feature Request</SelectItem>
                      <SelectItem value="general">📝 {language === 'fr' ? 'Général' : 'General'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Langue' : 'Language'}</Label>
                  <Select value={editingNote.language} onValueChange={(v) => setEditingNote({...editingNote, language: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">🇫🇷 Français</SelectItem>
                      <SelectItem value="en">🇬🇧 English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Contenu' : 'Content'} *</Label>
                <Textarea 
                  value={editingNote.content} 
                  onChange={(e) => setEditingNote({...editingNote, content: e.target.value})} 
                  placeholder={language === 'fr' ? 'Contenu de la note, conversation, détails techniques...' : 'Note content, conversation, technical details...'}
                  className="min-h-[400px] font-mono text-sm"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(false)}>{language === 'fr' ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleSaveNote} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Save className="h-4 w-4 mr-2" />{language === 'fr' ? 'Sauvegarder' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer to Knowledge Base Dialog */}
      <Dialog open={transferToKBDialog} onOpenChange={setTransferToKBDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-amber-600" />
              {language === 'fr' ? 'Transférer vers Base de connaissances' : 'Transfer to Knowledge Base'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Cette note sera copiée dans la section sélectionnée de la Base de connaissances.'
                : 'This note will be copied to the selected Knowledge Base section.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Selected Note Info */}
            <div className="p-3 bg-muted/50 rounded-lg border">
              <p className="text-sm font-medium truncate">{selectedNote?.title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedNote?.content?.substring(0, 100)}...
              </p>
            </div>

            {/* Select Existing Section */}
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Sélectionner une section existante' : 'Select existing section'}</Label>
              <Select 
                value={selectedKBSection} 
                onValueChange={(v) => { setSelectedKBSection(v); setNewKBSection(''); }}
                disabled={newKBSection.trim().length > 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={language === 'fr' ? 'Choisir une section...' : 'Choose a section...'} />
                </SelectTrigger>
                <SelectContent>
                  {kbSections.map((section) => (
                    <SelectItem key={section.section_id} value={section.section_id}>
                      {section.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t" />
              <span className="text-xs text-muted-foreground uppercase">{language === 'fr' ? 'ou' : 'or'}</span>
              <div className="flex-1 border-t" />
            </div>

            {/* Create New Section */}
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Créer une nouvelle section' : 'Create new section'}</Label>
              <Input 
                value={newKBSection}
                onChange={(e) => { setNewKBSection(e.target.value); setSelectedKBSection(''); }}
                placeholder={language === 'fr' ? 'Nom de la nouvelle section...' : 'New section name...'}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferToKBDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleTransferToKB} 
              disabled={transferringNote || (!selectedKBSection && !newKBSection.trim())}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {transferringNote && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <BookOpen className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Transférer' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WooCommerce Installation Guide */}
      <WooCommerceGuide 
        language="fr"
        isOpen={showWooGuide}
        onClose={() => setShowWooGuide(false)}
        adminKey={adminKey}
      />

      {/* New API Key Dialog */}
      <Dialog open={newApiKeyDialog} onOpenChange={setNewApiKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Key className="h-4 w-4 text-white" />
              </div>
              Nouvelle Clé API WooCommerce
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom de la boutique *</Label>
              <Input 
                value={newShopName}
                onChange={(e) => setNewShopName(e.target.value)}
                placeholder="Ma Boutique WooCommerce"
              />
            </div>
            <div className="space-y-2">
              <Label>URL de la boutique</Label>
              <Input 
                value={newShopUrl}
                onChange={(e) => setNewShopUrl(e.target.value)}
                placeholder="https://maboutique.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewApiKeyDialog(false)}>Annuler</Button>
            <Button onClick={createApiKey} disabled={creatingKey || !newShopName} className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
              {creatingKey && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Key className="h-4 w-4 mr-2" />
              Créer la Clé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Key Created Dialog */}
      {selectedApiKey?.api_key && (
        <Dialog open={!!selectedApiKey?.api_key} onOpenChange={() => setSelectedApiKey(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle className="h-6 w-6" />
                Clé API Créée!
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-amber-800 dark:text-amber-200 text-sm font-medium mb-2">
                  ⚠️ Copiez cette clé maintenant! Elle ne sera plus affichée.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Clé API</Label>
                <div className="flex gap-2">
                  <Input value={selectedApiKey.api_key} readOnly className="font-mono text-sm" />
                  <Button variant="outline" onClick={() => copyToClipboard(selectedApiKey.api_key)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Boutique</Label>
                <p className="text-sm font-medium">{selectedApiKey.shop_name}</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => { setSelectedApiKey(null); fetchApiKeys(); }}>
                J'ai copié la clé
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* IPN Configuration Dialog */}
      <Dialog open={ipnConfigDialog} onOpenChange={setIpnConfigDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Bell className="h-4 w-4 text-white" />
              </div>
              Configuration IPN - {selectedApiKey?.shop_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-blue-800 dark:text-blue-200 text-sm">
                <strong>IPN (Instant Payment Notification)</strong> permet à PayHub de notifier automatiquement votre boutique WooCommerce du statut des paiements.
              </p>
            </div>
            <div className="space-y-2">
              <Label>URL IPN de votre boutique WooCommerce</Label>
              <Input 
                value={ipnUrl}
                onChange={(e) => setIpnUrl(e.target.value)}
                placeholder="https://votreboutique.com/?wc-api=payhub_ipn"
              />
              <p className="text-xs text-muted-foreground">
                Format: <code>https://votreboutique.com/?wc-api=payhub_ipn</code>
              </p>
            </div>
            {selectedApiKey?.ipn_secret && (
              <div className="space-y-2">
                <Label>Secret IPN (pour vérification)</Label>
                <div className="flex gap-2">
                  <Input value={selectedApiKey.ipn_secret} readOnly className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(selectedApiKey.ipn_secret)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Copiez ce secret dans les paramètres du plugin WooCommerce
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIpnConfigDialog(false)}>Annuler</Button>
            <Button onClick={configureIpn} disabled={!ipnUrl} className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
              <Bell className="h-4 w-4 mr-2" />
              Configurer IPN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Invitation Dialog */}
      <Dialog open={invitationDialog} onOpenChange={setInvitationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-amber-500" />
              {language === 'fr' ? 'Nouvelle Invitation d\'Agence' : 'New Agency Invitation'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Créez une invitation pour qu\'une nouvelle agence rejoigne votre réseau PayHub Québec+.'
                : 'Create an invitation for a new agency to join your PayHub Québec+ network.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inv-email">{language === 'fr' ? 'Email du contact *' : 'Contact Email *'}</Label>
              <Input 
                id="inv-email"
                type="email"
                placeholder="contact@agence.ca"
                value={newInvitation.email}
                onChange={(e) => setNewInvitation({ ...newInvitation, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inv-agency">{language === 'fr' ? 'Nom de l\'agence (optionnel)' : 'Agency Name (optional)'}</Label>
              <Input 
                id="inv-agency"
                placeholder={language === 'fr' ? 'Ex: Caisse Locale de Montréal' : 'Ex: Montreal Local Credit Union'}
                value={newInvitation.agency_name}
                onChange={(e) => setNewInvitation({ ...newInvitation, agency_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inv-msg">{language === 'fr' ? 'Message personnalisé (optionnel)' : 'Custom Message (optional)'}</Label>
              <Textarea 
                id="inv-msg"
                rows={3}
                placeholder={language === 'fr' ? 'Un message pour accueillir la nouvelle agence...' : 'A welcome message for the new agency...'}
                value={newInvitation.message}
                onChange={(e) => setNewInvitation({ ...newInvitation, message: e.target.value })}
              />
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <p className="text-sm text-green-700 dark:text-green-300">
                <strong>📧 {language === 'fr' ? 'Email automatique:' : 'Automatic email:'}</strong>{' '}
                {language === 'fr' 
                  ? 'Un email d\'invitation sera envoyé automatiquement au contact avec le lien pour créer son agence.'
                  : 'An invitation email will be automatically sent to the contact with the link to create their agency.'
                }
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvitationDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={createInvitation}
              disabled={creatingInvitation || !newInvitation.email}
              className="bg-gradient-to-r from-amber-500 to-orange-600"
            >
              {creatingInvitation && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Send className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Créer l\'invitation' : 'Create Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agency Details Dialog */}
      <Dialog open={!!selectedAgency} onOpenChange={() => setSelectedAgency(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white font-bold">
                {selectedAgency?.name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              {selectedAgency?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedAgency?.city && selectedAgency?.region 
                ? `${selectedAgency.city}, ${selectedAgency.region}`
                : selectedAgency?.city || selectedAgency?.region || 'N/A'
              }
            </DialogDescription>
          </DialogHeader>
          {selectedAgency && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">{language === 'fr' ? 'Statut' : 'Status'}</Label>
                  <span className={`inline-block px-2 py-1 rounded-full text-sm ${
                    selectedAgency.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedAgency.status}
                  </span>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">{language === 'fr' ? 'Utilisateurs' : 'Users'}</Label>
                  <p className="font-semibold">{selectedAgency.user_count || 0}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">{language === 'fr' ? 'Devise Locale' : 'Local Currency'}</Label>
                  <p className="font-semibold">{selectedAgency.currency?.name || 'Dollar Local'} ({selectedAgency.currency?.symbol || '$'})</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">{language === 'fr' ? 'Slug URL' : 'URL Slug'}</Label>
                  <p className="font-mono text-sm">/agency/{selectedAgency.slug}</p>
                </div>
              </div>
              <div className="border-t pt-4">
                <Label className="text-muted-foreground text-xs uppercase mb-2 block">{language === 'fr' ? 'Contact' : 'Contact'}</Label>
                <div className="space-y-2 text-sm">
                  {selectedAgency.contact?.email && <p><Mail className="h-4 w-4 inline mr-2" />{selectedAgency.contact.email}</p>}
                  {selectedAgency.contact?.phone && <p><span className="mr-2">📞</span>{selectedAgency.contact.phone}</p>}
                  {selectedAgency.contact?.address && <p><MapPin className="h-4 w-4 inline mr-2" />{selectedAgency.contact.address}</p>}
                </div>
              </div>
              {selectedAgency.description && (
                <div className="border-t pt-4">
                  <Label className="text-muted-foreground text-xs uppercase mb-2 block">{language === 'fr' ? 'Description' : 'Description'}</Label>
                  <p className="text-sm">{selectedAgency.description}</p>
                </div>
              )}
              <div className="border-t pt-4">
                <Label className="text-muted-foreground text-xs uppercase mb-2 block">{language === 'fr' ? 'Créée le' : 'Created'}</Label>
                <p className="text-sm">{new Date(selectedAgency.created_at).toLocaleString()}</p>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => window.open(`/agency/${selectedAgency?.slug}`, '_blank')}
              className="w-full sm:w-auto text-pink-600 border-pink-200 hover:bg-pink-50"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Voir la page publique' : 'View Public Page'}
            </Button>
            <Button variant="outline" onClick={() => setSelectedAgency(null)} className="w-full sm:w-auto">
              {language === 'fr' ? 'Fermer' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AdminProvider>
  );
}
